/* GETTMI bag: the native brand-grouped bag and Saved for later flow.
   No checkout exists; the bag saves pieces and hands the shopper to each
   brand's own site. Signed-in state mirrors the app's bag_items rows.
   Storage shape: { version: 1, items: [], savedItems: [], pendingRemovals: {} }. */

(function () {
  var LEGACY_STORAGE_KEY = 'gettmi:bag:v1';
  var STORAGE_KEY = 'gettmi:bag:v2';
  var SESSION_KEY = 'gettmi:session:v1';
  var SAVE_FOR_LATER_AFTER_MS = 24 * 60 * 60 * 1000;
  var perfectLookProducts = [];
  var perfectLookLoadId = 0;
  var selectedBagBrand = null;
  var modelImageCache = {};
  var bagActionMessage = '';

  function config() {
    var node = document.querySelector('[data-gettmi-supabase]');
    if (!node) return null;
    return {
      url: String(node.dataset.supabaseUrl || '').replace(/\/+$/, ''),
      key: String(node.dataset.supabaseKey || '')
    };
  }

  function readSession() {
    try {
      var raw = window.localStorage.getItem(SESSION_KEY);
      var session = raw ? JSON.parse(raw) : null;
      if (!session || !session.access_token || !session.user_id) return null;
      if (session.expires_at && session.expires_at * 1000 <= Date.now() + 30000) return null;
      return session;
    } catch (error) {
      return null;
    }
  }

  function authHeaders(cfg, session) {
    return {
      apikey: cfg.key,
      Authorization: 'Bearer ' + session.access_token
    };
  }

  function emptyBag() {
    return { version: 1, items: [], savedItems: [], pendingRemovals: {} };
  }

  function guestStorageKey() {
    return STORAGE_KEY + ':guest';
  }

  function userStorageKey(userId) {
    return STORAGE_KEY + ':user:' + encodeURIComponent(String(userId || ''));
  }

  function activeStorageKey() {
    var session = readSession();
    return session && session.user_id ? userStorageKey(session.user_id) : guestStorageKey();
  }

  function normaliseBag(data) {
    if (!data || !Array.isArray(data.items)) return emptyBag();
    var activeItems = [];
    var savedItems = Array.isArray(data.savedItems) ? data.savedItems.slice() : [];
    data.items.forEach(function (item) {
      if (item && item.status === 'saved') {
        savedItems.push(item);
      } else if (item) {
        activeItems.push(item);
      }
    });
    return {
      version: 1,
      items: activeItems,
      savedItems: savedItems,
      pendingRemovals:
        data.pendingRemovals && typeof data.pendingRemovals === 'object' ? data.pendingRemovals : {}
    };
  }

  /* A removal made while the connection is down never reaches the account. Recording it
     here means the next sync retries it, instead of the account's surviving row being
     pulled back down and the item reappearing. */
  function recordPendingRemoval(item) {
    var wanted = variantKey(item);
    if (!wanted) return;
    var bag = readBag();
    bag.pendingRemovals[wanted] = new Date().toISOString();
    writeBag(bag);
  }

  function clearPendingRemoval(item) {
    var wanted = variantKey(item);
    if (!wanted) return;
    var bag = readBag();
    if (!bag.pendingRemovals[wanted]) return;
    delete bag.pendingRemovals[wanted];
    writeBag(bag);
  }

  function readBagAt(key) {
    try {
      var raw = window.localStorage.getItem(key);
      return normaliseBag(raw ? JSON.parse(raw) : null);
    } catch (error) {
      return emptyBag();
    }
  }

  function writeBagAt(key, bag) {
    try {
      window.localStorage.setItem(key, JSON.stringify(normaliseBag(bag)));
    } catch (error) {
      /* Storage can be unavailable; the current interaction still completes. */
    }
  }

  function migrateLegacyStorage() {
    try {
      var legacy = window.localStorage.getItem(LEGACY_STORAGE_KEY);
      if (!legacy) return;
      var session = readSession();
      if (!session && !window.localStorage.getItem(guestStorageKey())) {
        window.localStorage.setItem(guestStorageKey(), legacy);
      }
      window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch (error) {
      /* Never attach ambiguous legacy state to an authenticated owner. */
    }
  }

  function readBag() {
    migrateLegacyStorage();
    return readBagAt(activeStorageKey());
  }

  function writeBag(bag) {
    var nextBag = normaliseBag(bag);
    writeBagAt(activeStorageKey(), nextBag);
    updateCount();
    document.dispatchEvent(new CustomEvent('gettmi:bag:changed', {
      detail: {
        count: nextBag.items.length,
        savedCount: nextBag.savedItems.length
      }
    }));
  }

  function mergeBagState(target, source) {
    var merged = normaliseBag(target);
    normaliseBag(source).items.concat(normaliseBag(source).savedItems).forEach(function (candidate) {
      if (!variantKey(candidate)) return;
      var existing = merged.items.concat(merged.savedItems).filter(function (item) {
        return variantKey(item) === variantKey(candidate);
      })[0];
      if (existing && itemTime(existing) >= itemTime(candidate)) return;
      merged.items = removeVariant(merged.items, candidate);
      merged.savedItems = removeVariant(merged.savedItems, candidate);
      if (candidate.status === 'saved') merged.savedItems.push(candidate);
      else merged.items.push(candidate);
    });
    return merged;
  }

  function prepareForSession(userId) {
    if (!userId) return;
    migrateLegacyStorage();
    var guestKey = guestStorageKey();
    var ownerKey = userStorageKey(userId);
    var guestBag = readBagAt(guestKey);
    if (guestBag.items.length || guestBag.savedItems.length) {
      writeBagAt(ownerKey, mergeBagState(readBagAt(ownerKey), guestBag));
      try {
        window.localStorage.removeItem(guestKey);
      } catch (error) {
        /* Scoped owner storage still prevents another account reading the guest state. */
      }
    }
    updateCount();
    renderBagPage();
  }

  function clearSessionStorage(userId) {
    try {
      if (userId) window.localStorage.removeItem(userStorageKey(userId));
      window.localStorage.removeItem(guestStorageKey());
      window.localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch (error) {
      /* The UI is still reset even when browser storage is unavailable. */
    }
    selectedBagBrand = null;
    bagActionMessage = '';
    updateCount();
    renderBagPage();
  }

  function itemKey(item) {
    return item.handle + '::' + (item.size || '');
  }

  function variantKey(item) {
    return String(item.variantId || item.key || item.handle || '');
  }

  function itemTime(item) {
    var value = item && (item.updatedAt || item.addedAt);
    var time = value ? new Date(value).getTime() : 0;
    return Number.isFinite(time) ? time : 0;
  }

  function removeVariant(items, item) {
    var wanted = variantKey(item);
    return items.filter(function (candidate) {
      return variantKey(candidate) !== wanted;
    });
  }

  function remotePayload(item, status, session) {
    return {
      user_id: session.user_id,
      variant_id: variantKey(item),
      product_handle: item.handle || '',
      product_path: item.url || (item.handle ? '/products/' + item.handle : ''),
      product_title: item.title || '',
      vendor: item.vendor || '',
      image: item.image || '',
      image_alt: item.imageAlt || '',
      price: item.priceCents != null ? String(item.priceCents) : String(item.price || ''),
      size: item.size || '',
      quantity: Number(item.quantity) > 0 ? Math.floor(Number(item.quantity)) : 1,
      status: status,
      button_url: item.handoffUrl || '',
      source: 'web',
      deleted_at: null,
      updated_at: item.updatedAt || new Date().toISOString()
    };
  }

  function persistItem(item, status) {
    var cfg = config();
    var session = readSession();
    if (!cfg || !session || !variantKey(item)) return Promise.resolve(true);
    return window.fetch(cfg.url + '/rest/v1/bag_items?on_conflict=user_id,variant_id', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: cfg.key,
        Authorization: 'Bearer ' + session.access_token,
        Prefer: 'resolution=merge-duplicates'
      },
      body: JSON.stringify(remotePayload(item, status, session))
    }).then(function (response) {
      if (response.ok) markSynced(item);
      return response.ok;
    }).catch(function () {
      return false;
    });
  }

  /* Once the account has accepted an item, a later "missing from the account" means it was
     removed on another device. Without this flag that is indistinguishable from an upload
     that never landed, so the browser re-uploads its stale copy and undoes the removal. */
  function markSynced(item) {
    var wanted = variantKey(item);
    if (!wanted) return;
    var bag = readBag();
    var touched = false;
    bag.items.concat(bag.savedItems).forEach(function (candidate) {
      if (variantKey(candidate) === wanted && !candidate.syncedToAccount) {
        candidate.syncedToAccount = true;
        touched = true;
      }
    });
    if (touched) writeBag(bag);
  }

  function persistStatus(item, status) {
    var cfg = config();
    var session = readSession();
    if (!cfg || !session || !variantKey(item)) return Promise.resolve(true);
    return window.fetch(
      cfg.url + '/rest/v1/bag_items?user_id=eq.' + encodeURIComponent(session.user_id) +
        '&variant_id=eq.' + encodeURIComponent(variantKey(item)) + '&deleted_at=is.null',
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          apikey: cfg.key,
          Authorization: 'Bearer ' + session.access_token
        },
        body: JSON.stringify({ status: status, updated_at: item.updatedAt })
      }
    ).then(function (response) {
      return response.ok;
    }).catch(function () {
      return false;
    });
  }

  function persistDelete(item) {
    var cfg = config();
    var session = readSession();
    if (!cfg || !session || !variantKey(item)) return Promise.resolve(true);
    var now = new Date().toISOString();
    return window.fetch(
      cfg.url + '/rest/v1/bag_items?user_id=eq.' + encodeURIComponent(session.user_id) +
        '&variant_id=eq.' + encodeURIComponent(variantKey(item)),
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          apikey: cfg.key,
          Authorization: 'Bearer ' + session.access_token,
          Prefer: 'return=minimal'
        },
        body: JSON.stringify({ deleted_at: now, updated_at: now })
      }
    ).then(function (response) {
      if (response.ok) clearPendingRemoval(item);
      return response.ok;
    }).catch(function () {
      return false;
    });
  }

  function addItem(item, options) {
    var opts = options || {};
    var bag = readBag();
    var now = new Date().toISOString();
    var nextItem = Object.assign({}, item, {
      key: item.key || itemKey(item),
      status: 'active',
      addedAt: item.addedAt || item.updatedAt || now,
      updatedAt: item.updatedAt || now
    });
    var wantedVariant = variantKey(nextItem);
    var exists = bag.items.some(function (candidate) {
      return candidate.key === nextItem.key || variantKey(candidate) === wantedVariant;
    });
    var wasSaved = bag.savedItems.some(function (candidate) {
      return candidate.key === nextItem.key || variantKey(candidate) === wantedVariant;
    });
    bag.savedItems = removeVariant(bag.savedItems, nextItem);
    if (!exists) bag.items.push(nextItem);
    writeBag(bag);
    if (opts.persist !== false && (!exists || wasSaved)) persistItem(nextItem, 'active');
    return !exists || wasSaved;
  }

  function removeItem(key) {
    var bag = readBag();
    var removed = bag.items.filter(function (item) { return item.key === key; })[0];
    bag.items = bag.items.filter(function (i) { return i.key !== key; });
    writeBag(bag);
    if (removed) {
      recordPendingRemoval(removed);
      persistDelete(removed);
    }
  }

  function saveItemForLater(key) {
    var bag = readBag();
    var item = bag.items.filter(function (candidate) { return candidate.key === key; })[0];
    if (!item) return;
    var savedItem = Object.assign({}, item, {
      status: 'saved',
      updatedAt: new Date().toISOString()
    });
    bag.items = bag.items.filter(function (candidate) { return candidate.key !== key; });
    bag.savedItems = removeVariant(bag.savedItems, savedItem);
    bag.savedItems.unshift(savedItem);
    bagActionMessage = 'Saved for later.';
    writeBag(bag);
    persistStatus(savedItem, 'saved');
  }

  function moveSavedToBag(key) {
    var bag = readBag();
    var item = bag.savedItems.filter(function (candidate) { return candidate.key === key; })[0];
    if (!item) return;
    var activeItem = Object.assign({}, item, {
      status: 'active',
      updatedAt: new Date().toISOString()
    });
    bag.savedItems = bag.savedItems.filter(function (candidate) { return candidate.key !== key; });
    bag.items = removeVariant(bag.items, activeItem);
    bag.items.push(activeItem);
    bagActionMessage = 'Moved to your bag.';
    writeBag(bag);
    persistStatus(activeItem, 'active');
  }

  function removeSavedItem(key) {
    var bag = readBag();
    var removed = bag.savedItems.filter(function (item) { return item.key === key; })[0];
    bag.savedItems = bag.savedItems.filter(function (item) { return item.key !== key; });
    bagActionMessage = 'Removed from Saved for later.';
    writeBag(bag);
    if (removed) {
      recordPendingRemoval(removed);
      persistDelete(removed);
    }
  }

  function autoSaveStaleItems() {
    var bag = readBag();
    var threshold = Date.now() - SAVE_FOR_LATER_AFTER_MS;
    var stale = bag.items.filter(function (item) {
      var updatedAt = itemTime(item);
      return updatedAt > 0 && updatedAt < threshold;
    });
    if (!stale.length) return 0;
    stale.forEach(function (item) {
      var savedItem = Object.assign({}, item, {
        status: 'saved',
        updatedAt: new Date().toISOString()
      });
      bag.items = removeVariant(bag.items, item);
      bag.savedItems = removeVariant(bag.savedItems, item);
      bag.savedItems.unshift(savedItem);
      persistStatus(savedItem, 'saved');
    });
    bagActionMessage = 'Moved ' + stale.length + ' item' + (stale.length === 1 ? '' : 's') +
      ' you haven’t touched in a while to Saved for later.';
    writeBag(bag);
    return stale.length;
  }

  function mapRemoteItem(row) {
    var rawPrice = String(row.price == null ? '' : row.price);
    var numericPrice = /^[0-9]+$/.test(rawPrice) ? Number(rawPrice) : null;
    var item = {
      handle: row.product_handle || '',
      variantId: String(row.variant_id || ''),
      title: row.product_title || '',
      vendor: row.vendor || '',
      url: row.product_path || (row.product_handle ? '/products/' + row.product_handle : ''),
      image: normaliseImage(row.image || ''),
      imageAlt: normaliseImage(row.image_alt || ''),
      price: numericPrice === null ? rawPrice : formatMoney(numericPrice),
      priceCents: numericPrice,
      size: row.size || '',
      quantity: Number(row.quantity) > 0 ? Math.floor(Number(row.quantity)) : 1,
      handoffUrl: row.button_url || '',
      status: row.status === 'saved' ? 'saved' : 'active',
      addedAt: row.updated_at || new Date().toISOString(),
      updatedAt: row.updated_at || new Date().toISOString()
    };
    item.key = itemKey(item);
    return item;
  }

  function mergeRemoteItems(rows, options) {
    if (!Array.isArray(rows)) return Promise.resolve(false);
    /* A fresh sign-in adopts whatever the browser collected while signed out. Every other
       load treats the account as the truth, so removals made in the app are not undone. */
    var freshSignIn = Boolean(options && options.freshSignIn);
    var bag = readBag();
    var remoteVariants = {};
    var pending = [];
    var dropped = [];

    rows.forEach(function (row) {
      var remoteItem = mapRemoteItem(row);
      var wanted = variantKey(remoteItem);
      if (!wanted) return;
      remoteVariants[wanted] = true;

      var pendingRemovedAt = bag.pendingRemovals[wanted];
      if (pendingRemovedAt) {
        if (itemTime({ updatedAt: pendingRemovedAt }) >= itemTime(remoteItem)) {
          pending.push(persistDelete(remoteItem));
          return;
        }
        /* The account has a newer change, so this browser's offline removal is stale. */
        delete bag.pendingRemovals[wanted];
      }

      var localItem = bag.items.concat(bag.savedItems).filter(function (candidate) {
        return variantKey(candidate) === wanted;
      })[0];

      if (localItem && itemTime(localItem) > itemTime(remoteItem)) {
        pending.push(
          persistItem(localItem, localItem.status === 'saved' ? 'saved' : 'active')
        );
        return;
      }

      bag.items = removeVariant(bag.items, remoteItem);
      bag.savedItems = removeVariant(bag.savedItems, remoteItem);
      remoteItem.syncedToAccount = true;
      if (remoteItem.status === 'saved') {
        bag.savedItems.push(remoteItem);
      } else {
        bag.items.push(remoteItem);
      }
    });

    /* Items the browser holds that the account does not. On a fresh sign-in they are the
       guest bag and belong to the account now. Otherwise one that previously reached the
       account was removed elsewhere and goes, while one that never reached it is an upload
       that failed and is retried, so nobody loses an item to a dropped connection. */
    bag.items.concat(bag.savedItems).forEach(function (localItem) {
      if (remoteVariants[variantKey(localItem)]) return;
      if (freshSignIn || !localItem.syncedToAccount) {
        pending.push(
          persistItem(localItem, localItem.status === 'saved' ? 'saved' : 'active')
        );
        return;
      }
      dropped.push(localItem);
    });

    dropped.forEach(function (localItem) {
      bag.items = removeVariant(bag.items, localItem);
      bag.savedItems = removeVariant(bag.savedItems, localItem);
    });

    writeBag(bag);
    updateCount();
    autoSaveStaleItems();
    renderBagPage();
    return Promise.all(pending).then(function (results) {
      return results.every(function (result) { return result === true; });
    });
  }

  function groupByBrand() {
    var groups = {};
    readBag().items.forEach(function (item) {
      var brand = item.vendor || 'OTHER';
      (groups[brand] = groups[brand] || []).push(item);
    });
    return groups;
  }

  function formatBagTimeAgo(iso) {
    if (!iso) return 'Just added';
    var then = new Date(iso).getTime();
    if (Number.isNaN(then)) return 'Just added';
    var minutes = Math.floor((Date.now() - then) / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return minutes + ' ' + (minutes === 1 ? 'minute' : 'minutes') + ' ago';
    var hours = Math.floor(minutes / 60);
    if (hours < 24) return hours + ' ' + (hours === 1 ? 'hour' : 'hours') + ' ago';
    var days = Math.floor(hours / 24);
    return days + ' ' + (days === 1 ? 'day' : 'days') + ' ago';
  }

  function itemSubtotal(items) {
    return items.reduce(function (total, item) {
      return total + Number(item.priceCents || 0);
    }, 0);
  }

  function primeModelImages(items) {
    var shopRoot = window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.root
      : '/';
    var pending = items.filter(function (item) {
      return item.handle && modelImageCache[item.handle] === undefined;
    });
    if (!pending.length) return;

    pending.forEach(function (item) {
      modelImageCache[item.handle] = '';
      window.fetch(shopRoot + 'products/' + encodeURIComponent(item.handle) + '.js')
        .then(function (response) {
          if (!response.ok) return null;
          return response.json();
        })
        .then(function (product) {
          var images = product && Array.isArray(product.images) ? product.images : [];
          modelImageCache[item.handle] = normaliseImage(images[1] || images[0] || item.image);
          renderBagPage();
        })
        .catch(function () {
          modelImageCache[item.handle] = normaliseImage(item.image);
        });
    });
  }

  function updateCount() {
    var count = readBag().items.length;
    document.querySelectorAll('[data-gettmi-bag-count]').forEach(function (el) {
      el.textContent = count > 0 ? String(count) : '';
      el.toggleAttribute('hidden', count === 0);
    });
  }

  /* Tracking: every brand handoff fires an event; adds UTM referral markers
     so brands can see GETTMI traffic. Commission wiring arrives with the
     affiliate-network workstream; this proves the clicks meanwhile. */
  function decorateHandoffUrl(url) {
    try {
      var u = new URL(url);
      if (!u.searchParams.has('utm_source')) {
        u.searchParams.set('utm_source', 'gettmi');
        u.searchParams.set('utm_medium', 'referral');
      }
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  function trackHandoff(detail) {
    if (
      typeof window.gettmiAnalyticsAllowed === 'function' &&
      window.gettmiAnalyticsAllowed() &&
      typeof window.gtag === 'function'
    ) {
      window.gtag('event', 'gettmi_handoff', {
        brand: detail.vendor || '',
        product_handle: detail.handle || '',
        price: detail.price || '',
        placement: detail.placement || 'pdp'
      });
    }
  }

  function handoffNudgeQualifies(handle, placement) {
    if (placement === 'bag') return readBag().items.length > 0;
    if (!handle) return false;
    var inBag = readBag().items.some(function (item) {
      return item.handle === handle;
    });
    var favourites =
      window.GettmiFavourites &&
      typeof window.GettmiFavourites.read === 'function'
        ? window.GettmiFavourites.read()
        : {};
    return inBag || Boolean(favourites && favourites[handle]);
  }

  function selectedSize(root) {
    var section = root.closest('section, .product') || document;
    var checked = section.querySelector('fieldset input[type="radio"]:checked');
    if (checked) return checked.value;
    var select = section.querySelector('.product-form__input select, select[name^="options"]');
    return select ? select.value : '';
  }

  function itemFromBuyBox(box) {
    return {
      handle: box.dataset.productHandle,
      variantId: box.dataset.variantId,
      title: box.dataset.productTitle,
      vendor: box.dataset.productVendor,
      url: box.dataset.productUrl,
      image: box.dataset.productImage,
      price: box.dataset.productPrice,
      priceCents: parseInt(box.dataset.productPriceCents || '0', 10),
      handoffUrl: box.dataset.handoffUrl || '',
      size: selectedSize(box)
    };
  }

  function normaliseImage(value) {
    var image = String(value || '');
    return image.indexOf('//') === 0 ? 'https:' + image : image;
  }

  function formatMoney(cents) {
    var currency = window.Shopify && window.Shopify.currency
      ? window.Shopify.currency.active
      : 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return '£' + (Number(cents || 0) / 100).toFixed(2);
    }
  }

  function ageFromDate(value) {
    if (!value) return null;
    var dob = new Date(String(value) + 'T12:00:00');
    if (Number.isNaN(dob.getTime())) return null;
    var today = new Date();
    var age = today.getFullYear() - dob.getFullYear();
    var beforeBirthday = today.getMonth() < dob.getMonth() ||
      (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate());
    return age - (beforeBirthday ? 1 : 0);
  }

  function isApprovedPerfectLookRail(row) {
    var signals = row && row.source_signals ? row.source_signals : {};
    var expiresAt = row && row.expires_at ? new Date(row.expires_at).getTime() : null;
    var activeStatuses = ['approved', 'published', 'app_published', 'active'];
    return Boolean(
      row &&
      row.rail_key === 'perfect_the_look' &&
      signals.source === 'gm_engine' &&
      activeStatuses.indexOf(String(signals.publication_status || '').toLowerCase()) !== -1 &&
      signals.requires_explicit_publish_approval !== true &&
      (!expiresAt || expiresAt > Date.now())
    );
  }

  function bagHandles() {
    return new Set(readBag().items.map(function (item) {
      return item.handle || String(item.url || '').split('/products/')[1] || '';
    }).filter(Boolean));
  }

  function mapPerfectLookProduct(product) {
    if (!product || !product.handle) return null;
    var variants = Array.isArray(product.variants) ? product.variants : [];
    var variant = variants.filter(function (item) { return item.available !== false; })[0] || variants[0];
    var image = normaliseImage(
      (Array.isArray(product.images) && product.images[0]) || product.featured_image
    );
    if (!variant || !variant.id || !image) return null;
    var priceCents = Number(variant.price || product.price || 0);
    return {
      handle: product.handle,
      variantId: String(variant.id),
      title: product.title || '',
      vendor: product.vendor || '',
      url: product.url || '/products/' + product.handle,
      image: image,
      price: formatMoney(priceCents),
      priceCents: priceCents,
      handoffUrl: '',
      size: variant.public_title || variant.title || 'Default'
    };
  }

  function renderPerfectLook() {
    var mount = document.querySelector('[data-gettmi-bag-page]');
    var panel = mount && mount.querySelector('[data-gettmi-perfect-look]');
    var rail = panel && panel.querySelector('[data-gettmi-perfect-look-rail]');
    if (!panel || !rail) return;

    var inBag = bagHandles();
    var visible = perfectLookProducts.filter(function (product) {
      return !inBag.has(product.handle);
    }).slice(0, 6);
    panel.hidden = !selectedBagBrand || visible.length === 0 || readBag().items.length === 0;
    if (panel.hidden) {
      rail.innerHTML = '';
      return;
    }

    rail.innerHTML = visible.map(function (product) {
      return (
        '<article class="gettmi-perfect-look__card" role="listitem">' +
          '<a class="gettmi-perfect-look__media" href="' + escapeHtml(product.url) + '">' +
            '<img src="' + escapeHtml(product.image) + '" alt="" width="232" height="276" loading="lazy">' +
          '</a>' +
          '<p class="gettmi-perfect-look__vendor">' + escapeHtml(product.vendor) + '</p>' +
          '<a class="gettmi-perfect-look__name" href="' + escapeHtml(product.url) + '">' + escapeHtml(product.title) + '</a>' +
          '<div class="gettmi-perfect-look__footer">' +
            '<span>' + escapeHtml(product.price) + '</span>' +
            '<button type="button" data-gettmi-perfect-add="' + escapeHtml(product.variantId) + '">Add</button>' +
          '</div>' +
        '</article>'
      );
    }).join('');
  }

  function clearPerfectLook() {
    perfectLookLoadId += 1;
    perfectLookProducts = [];
    renderPerfectLook();
  }

  function loadPerfectLook() {
    var mount = document.querySelector('[data-gettmi-bag-page]');
    if (!mount || readBag().items.length === 0) {
      clearPerfectLook();
      return;
    }
    var cfg = config();
    var session = readSession();
    if (!cfg || !session) {
      clearPerfectLook();
      return;
    }

    perfectLookLoadId += 1;
    var loadId = perfectLookLoadId;
    var headers = authHeaders(cfg, session);
    var userId = encodeURIComponent(session.user_id);
    var profileRequest = window.fetch(
      cfg.url + '/rest/v1/profiles?select=date_of_birth&id=eq.' + userId + '&limit=1',
      { headers: headers }
    );
    var consentRequest = window.fetch(
      cfg.url + '/rest/v1/user_consents?select=granted&user_id=eq.' + userId +
        '&consent_key=eq.gettmi_personalisation&limit=1',
      { headers: headers }
    );
    var railsRequest = window.fetch(
      cfg.url + '/rest/v1/recommendation_rails?select=rail_key,title,subtitle,product_handles,reason_codes,source_signals,generated_at,expires_at,publish_run_key&user_id=eq.' +
        userId + '&order=generated_at.desc',
      { headers: headers }
    );

    Promise.all([profileRequest, consentRequest, railsRequest]).then(function (responses) {
      if (responses.some(function (response) { return !response.ok; })) throw new Error();
      return Promise.all(responses.map(function (response) { return response.json(); }));
    }).then(function (results) {
      if (loadId !== perfectLookLoadId) return [];
      var profile = Array.isArray(results[0]) && results[0].length ? results[0][0] : {};
      var consent = Array.isArray(results[1]) && results[1].length ? results[1][0] : null;
      var age = ageFromDate(profile.date_of_birth);
      if ((age !== null && age < 18) || (consent && consent.granted === false)) return [];

      var rails = Array.isArray(results[2]) ? results[2] : [];
      var rail = rails.filter(isApprovedPerfectLookRail)[0];
      var inBag = bagHandles();
      var handles = rail && Array.isArray(rail.product_handles)
        ? rail.product_handles.filter(function (handle) { return handle && !inBag.has(handle); }).slice(0, 6)
        : [];
      var shopRoot = window.Shopify && window.Shopify.routes
        ? window.Shopify.routes.root
        : '/';
      return Promise.all(handles.map(function (handle) {
        return window.fetch(shopRoot + 'products/' + encodeURIComponent(handle) + '.js')
          .then(function (response) {
            if (!response.ok) return null;
            return response.json();
          })
          .then(mapPerfectLookProduct)
          .catch(function () { return null; });
      }));
    }).then(function (products) {
      if (loadId !== perfectLookLoadId) return;
      perfectLookProducts = Array.isArray(products) ? products.filter(Boolean) : [];
      renderPerfectLook();
    }).catch(function () {
      if (loadId !== perfectLookLoadId) return;
      perfectLookProducts = [];
      renderPerfectLook();
    });
  }

  document.addEventListener('click', function (event) {
    var bagBack = event.target.closest('[data-gettmi-bag-back]');
    if (bagBack) {
      selectedBagBrand = null;
      renderBagPage();
      return;
    }

    var bagCover = event.target.closest('[data-gettmi-bag-brand]');
    if (bagCover) {
      selectedBagBrand = bagCover.dataset.gettmiBagBrand || null;
      renderBagPage();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    var perfectAdd = event.target.closest('[data-gettmi-perfect-add]');
    if (perfectAdd) {
      var product = perfectLookProducts.filter(function (item) {
        return item.variantId === perfectAdd.dataset.gettmiPerfectAdd;
      })[0];
      if (!product) return;
      addItem(product);
      renderBagPage();
      renderPerfectLook();
      loadPerfectLook();
      return;
    }

    var addButton = event.target.closest('[data-gettmi-add-to-bag]');
    if (addButton) {
      var box = addButton.closest('[data-gettmi-buy]');
      if (!box) return;
      addItem(itemFromBuyBox(box));
      var confirm = box.querySelector('[data-gettmi-bag-confirm]');
      if (confirm) {
        confirm.hidden = false;
        window.setTimeout(function () { confirm.hidden = true; }, 2400);
      }
      return;
    }

    var handoff = event.target.closest('[data-gettmi-handoff]');
    if (handoff) {
      handoff.href = decorateHandoffUrl(handoff.getAttribute('href'));
      var buyBox = handoff.closest('[data-gettmi-buy]');
      var handoffDetail = {
        vendor: handoff.dataset.vendor || (buyBox && buyBox.dataset.productVendor) || '',
        handle: handoff.dataset.handle || (buyBox && buyBox.dataset.productHandle) || '',
        price: buyBox ? buyBox.dataset.productPrice : '',
        placement: handoff.dataset.placement || (buyBox ? 'pdp' : 'bag')
      };
      trackHandoff(handoffDetail);
      document.dispatchEvent(new CustomEvent('gettmi:handoff-start', {
        detail: {
          qualifies: handoffNudgeQualifies(
            handoffDetail.handle,
            handoffDetail.placement
          )
        }
      }));
    }

    var removeButton = event.target.closest('[data-gettmi-remove]');
    if (removeButton) {
      removeItem(removeButton.dataset.gettmiRemove);
      renderBagPage();
      return;
    }

    var saveButton = event.target.closest('[data-gettmi-save-for-later]');
    if (saveButton) {
      saveItemForLater(saveButton.dataset.gettmiSaveForLater);
      selectedBagBrand = null;
      renderBagPage();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    var moveButton = event.target.closest('[data-gettmi-move-to-bag]');
    if (moveButton) {
      moveSavedToBag(moveButton.dataset.gettmiMoveToBag);
      renderBagPage();
      return;
    }

    var removeSavedButton = event.target.closest('[data-gettmi-remove-saved]');
    if (removeSavedButton) {
      removeSavedItem(removeSavedButton.dataset.gettmiRemoveSaved);
      renderBagPage();
    }
  });

  function renderBagActionStatus(mount) {
    var status = mount.querySelector('[data-gettmi-bag-status]');
    if (!status) return;
    status.textContent = bagActionMessage;
    status.hidden = !bagActionMessage;
  }

  function renderSavedForLater(mount, savedItems) {
    var panel = mount.querySelector('[data-gettmi-saved-for-later]');
    var list = panel && panel.querySelector('[data-gettmi-saved-for-later-list]');
    if (!panel || !list) return;
    panel.hidden = Boolean(selectedBagBrand) || savedItems.length === 0;
    if (panel.hidden) {
      list.innerHTML = '';
      return;
    }

    list.innerHTML = savedItems.map(function (item) {
      return (
        '<article class="gettmi-saved-later__item">' +
          '<a class="gettmi-saved-later__media" href="' + escapeHtml(item.url) + '">' +
            '<img src="' + escapeHtml(item.image) + '" alt="" width="92" height="132" loading="lazy">' +
          '</a>' +
          '<div class="gettmi-saved-later__info">' +
            '<a class="gettmi-saved-later__copy" href="' + escapeHtml(item.url) + '">' +
              '<span class="gettmi-saved-later__vendor">' + escapeHtml(item.vendor) + '</span>' +
              '<span class="gettmi-saved-later__title">' + escapeHtml(item.title) + '</span>' +
            '</a>' +
            (item.size ? '<p class="gettmi-saved-later__meta">Size ' + escapeHtml(item.size) + '</p>' : '') +
            '<p class="gettmi-saved-later__price">' + escapeHtml(item.price) + '</p>' +
            '<div class="gettmi-saved-later__actions">' +
              '<button type="button" data-gettmi-move-to-bag="' + escapeHtml(item.key) + '">Move to bag</button>' +
              '<button type="button" data-gettmi-remove-saved="' + escapeHtml(item.key) + '">Remove</button>' +
            '</div>' +
          '</div>' +
        '</article>'
      );
    }).join('');
  }

  /* Bag page rendering */
  function renderBagPage() {
    var mount = document.querySelector('[data-gettmi-bag-page]');
    if (!mount) return;
    var bag = readBag();
    var groups = groupByBrand();
    var brands = Object.keys(groups);
    var empty = mount.querySelector('[data-gettmi-bag-empty]');
    var list = mount.querySelector('[data-gettmi-bag-groups]');
    var intro = mount.querySelector('[data-gettmi-bag-intro]');
    var itemCount = mount.querySelector('[data-gettmi-bag-item-count]');
    var title = mount.querySelector('[data-gettmi-bag-title]');
    var back = mount.querySelector('[data-gettmi-bag-back]');
    if (!list) return;

    if (selectedBagBrand && !groups[selectedBagBrand]) selectedBagBrand = null;
    if (intro) intro.hidden = Boolean(selectedBagBrand);
    if (title) title.hidden = Boolean(selectedBagBrand);
    if (back) back.hidden = !selectedBagBrand;
    if (itemCount) {
      var count = bag.items.length;
      itemCount.textContent = count + ' ' + (count === 1 ? 'item' : 'items');
    }
    renderBagActionStatus(mount);
    renderSavedForLater(mount, bag.savedItems);

    if (brands.length === 0) {
      if (empty) empty.hidden = false;
      list.innerHTML = '';
      renderPerfectLook();
      return;
    }
    if (empty) empty.hidden = true;

    var allItems = readBag().items;
    primeModelImages(allItems);

    var shopAtTemplate = mount.dataset.shopAtLabel || 'SHOP AT %%BRAND%%';
    var removeLabel = mount.dataset.removeLabel || 'Remove';
    var disclosure = mount.dataset.disclosure || '';

    if (!selectedBagBrand) {
      list.innerHTML = brands.map(function (brand) {
        var items = groups[brand];
        var subtotal = itemSubtotal(items);
        var mostRecent = items.map(function (item) { return item.addedAt; })
          .filter(Boolean)
          .sort()
          .pop();
        var stackItems = items.slice(0, 3);
        var stackCenter = Math.floor((stackItems.length - 1) / 2);
        var coverImages = stackItems.map(function (item, index) {
          var image = modelImageCache[item.handle] || item.image;
          var rotation = (index - stackCenter) * 7;
          return '<img src="' + escapeHtml(image) + '" alt="" style="--gettmi-stack-rotation:' +
            rotation + 'deg;z-index:' + (index === stackCenter ? 3 : index + 1) + '" loading="lazy">';
        }).join('');

        return (
          '<button type="button" class="gettmi-bag-cover" data-gettmi-bag-brand="' + escapeHtml(brand) + '">' +
            '<span class="gettmi-bag-cover__top">' +
              '<span class="gettmi-bag-cover__meta"><span class="gettmi-bag-cover__time-icon"></span>' +
                escapeHtml(formatBagTimeAgo(mostRecent)) + '</span>' +
              '<span class="gettmi-bag-cover__meta"><span class="gettmi-bag-cover__count-icon"></span>' +
                items.length + '</span>' +
            '</span>' +
            '<span class="gettmi-bag-cover__brand">' + escapeHtml(brand) + '</span>' +
            '<span class="gettmi-bag-cover__sub">' + items.length + ' ' +
              (items.length === 1 ? 'item' : 'items') + ' · ' + escapeHtml(formatMoney(subtotal)) + '</span>' +
            '<span class="gettmi-bag-cover__stack">' + coverImages + '</span>' +
            '<span class="gettmi-bag-cover__cta">View bag →</span>' +
          '</button>'
        );
      }).join('');
      renderPerfectLook();
      return;
    }

    list.innerHTML = [selectedBagBrand].map(function (brand) {
      var items = groups[brand];
      var handoffUrl = '';
      for (var i = 0; i < items.length; i += 1) {
        if (items[i].handoffUrl) { handoffUrl = items[i].handoffUrl; break; }
      }
      var cards = items.map(function (item) {
        return (
          '<div class="gettmi-bag-item">' +
            '<a class="gettmi-bag-item__media" href="' + escapeHtml(item.url) + '"><img src="' + escapeHtml(item.image) + '" alt="" loading="lazy" width="150" height="200"></a>' +
            '<div class="gettmi-bag-item__info">' +
              '<a class="gettmi-bag-item__title" href="' + escapeHtml(item.url) + '">' + escapeHtml(item.title) + '</a>' +
              (item.size ? '<p class="gettmi-bag-item__size">Size ' + escapeHtml(item.size) + '</p>' : '') +
              '<p class="gettmi-bag-item__price">' + escapeHtml(item.price) + '</p>' +
              '<div class="gettmi-bag-item__actions">' +
                '<button type="button" class="gettmi-bag-item__remove" data-gettmi-remove="' + escapeHtml(item.key) + '">' + escapeHtml(removeLabel) + '</button>' +
                '<button type="button" class="gettmi-bag-item__save" data-gettmi-save-for-later="' + escapeHtml(item.key) + '">Save</button>' +
              '</div>' +
            '</div>' +
          '</div>'
        );
      }).join('');

      var brandLink = handoffUrl
        ? '<a class="button gettmi-get-now gettmi-bag-group__handoff" href="' + escapeHtml(handoffUrl) + '" target="_blank" rel="noopener sponsored nofollow" data-gettmi-handoff data-vendor="' + escapeHtml(brand) + '" data-placement="bag">' +
            escapeHtml(shopAtTemplate.replace('%%BRAND%%', brand)) + '</a>' +
          (disclosure ? '<p class="gettmi-disclosure">' + escapeHtml(disclosure) + '</p>' : '')
        : '';

      return (
        '<section class="gettmi-bag-group">' +
          '<div class="gettmi-bag-group__header">' +
            '<div class="gettmi-bag-group__header-copy">' +
              '<h2 class="gettmi-bag-group__brand">' + escapeHtml(brand) + '</h2>' +
              '<p class="gettmi-bag-group__meta">' + items.length + ' ' +
                (items.length === 1 ? 'item' : 'items') + ' · ' + escapeHtml(formatMoney(itemSubtotal(items))) + '</p>' +
            '</div>' +
            '<span class="gettmi-bag-group__badge">' + items.length + '</span>' +
          '</div>' +
          '<div class="gettmi-bag-group__items">' + cards + '</div>' +
          brandLink +
        '</section>'
      );
    }).join('');
    renderPerfectLook();
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  window.GettmiBag = {
    read: readBag,
    add: addItem,
    remove: removeItem,
    saveForLater: saveItemForLater,
    moveToBag: moveSavedToBag,
    removeSaved: removeSavedItem,
    mergeRemote: mergeRemoteItems,
    groups: groupByBrand,
    prepareForSession: prepareForSession,
    clearSessionStorage: clearSessionStorage
  };

  document.addEventListener('gettmi:signed-in', function (event) {
    var session = event.detail && event.detail.session || readSession();
    if (session && session.user_id) prepareForSession(session.user_id);
    loadPerfectLook();
  });
  document.addEventListener('gettmi:profile-updated', loadPerfectLook);
  document.addEventListener('gettmi:signed-out', function (event) {
    clearSessionStorage(event.detail && event.detail.userId);
    clearPerfectLook();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      updateCount();
      autoSaveStaleItems();
      renderBagPage();
      loadPerfectLook();
    });
  } else {
    updateCount();
    autoSaveStaleItems();
    renderBagPage();
    loadPerfectLook();
  }
})();
